import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
    FileSpreadsheet,
    Music,
    Download,
    Trash2,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    Filter,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AssetsPage = () => {
    const { user } = useAuth();
    const token = localStorage.getItem('token');
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [statusCounts, setStatusCounts] = useState({
        active: 0,
        pending: 0,
        deleted: 0,
    });
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    
    // Filters
    const [fileTypeFilter, setFileTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Delete request modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    
    // Approval modal
    const [approvalModalOpen, setApprovalModalOpen] = useState(false);
    const [approvalAction, setApprovalAction] = useState(null);

    const isFounder = user?.role === 'founder';

    const isExecutive = user?.role === 'executive';
    const [restrictedDialogOpen, setRestrictedDialogOpen] = useState(false);
    const fetchAssets = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/assets?page=${page}&limit=${limit}`;
            if (fileTypeFilter !== 'all') {
                url += `&file_type=${fileTypeFilter}`;
            }
            if (statusFilter !== 'all') {
                url += `&status=${statusFilter}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Failed to fetch assets');
            
            const data = await response.json();
            setAssets(data.assets);
            setTotal(data.total);
            setStatusCounts({
                active: data.active_count ?? 0,
                pending: data.pending_count ?? 0,
                deleted: data.deleted_count ?? 0,
            });
        } catch (error) {
            toast.error('Failed to load assets');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAssets();
    }, [page, fileTypeFilter, statusFilter, token]);

    const handleDownload = async (asset) => {
        try {
            const response = await fetch(`${API_URL}/api/assets/${asset.id}/download`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Failed to download');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = asset.filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success('Download started');
        } catch (error) {
            toast.error('Failed to download file');
            console.error(error);
        }
    };

    const openDeleteModal = (asset) => {
        setSelectedAsset(asset);
        setDeleteReason('');
        setDeleteModalOpen(true);
    };

    const handleRequestDelete = async () => {
        if (!deleteReason.trim()) {
            toast.error('Please provide a reason for deletion');
            return;
        }
        
        setSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/assets/${selectedAsset.id}/request-delete`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason: deleteReason })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to request deletion');
            }
            
            toast.success(isFounder ? 'Asset deleted successfully' : 'Delete request submitted for approval');
            setDeleteModalOpen(false);
            fetchAssets();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const openApprovalModal = (asset, action) => {
        setSelectedAsset(asset);
        setApprovalAction(action);
        setApprovalModalOpen(true);
    };

    const handleApproval = async () => {
        setSubmitting(true);
        try {
            const endpoint = approvalAction === 'approve' 
                ? `${API_URL}/api/assets/${selectedAsset.id}/approve-delete`
                : `${API_URL}/api/assets/${selectedAsset.id}/reject-delete`;
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to process request');
            }
            
            toast.success(approvalAction === 'approve' 
                ? 'Asset deleted successfully' 
                : 'Delete request rejected'
            );
            setApprovalModalOpen(false);
            fetchAssets();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
            case 'delete_requested':
                return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending Delete</Badge>;
            case 'deleted':
                return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Deleted</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getFileTypeIcon = (fileType) => {
        return fileType === 'revenue' 
            ? <FileSpreadsheet className="w-4 h-4 text-green-400" />
            : <Music className="w-4 h-4 text-purple-400" />;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold">Assets</h1>
                    <p className="text-muted-foreground">Upload history for content and revenue files</p>
                </div>
                <Button onClick={fetchAssets} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 p-4 bg-card rounded-lg border border-border/50">
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Filters:</span>
                </div>
                
                <div className="flex items-center gap-2">
                    <Label className="text-sm">Type:</Label>
                    <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
                        <SelectTrigger className="w-[140px] h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="content">Metadata</SelectItem>
                            <SelectItem value="revenue">Revenue</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                
                <div className="flex items-center gap-2">
                    <Label className="text-sm">Status:</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[160px] h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="delete_requested">Pending Delete</SelectItem>
                            <SelectItem value="deleted">Deleted</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-card rounded-lg border border-border/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <FileSpreadsheet className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{total}</div>
                            <div className="text-sm text-muted-foreground">Total Uploads</div>
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">
                                {statusCounts.active}
                            </div>
                            <div className="text-sm text-muted-foreground">Active</div>
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-500/10 rounded-lg">
                            <Clock className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">
                                {statusCounts.pending}
                            </div>
                            <div className="text-sm text-muted-foreground">Pending Deletion</div>
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/10 rounded-lg">
                            <Trash2 className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">
                                {statusCounts.deleted}
                            </div>
                            <div className="text-sm text-muted-foreground">Deleted</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assets Table */}
            <div className="bg-card rounded-lg border border-border/50 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : assets.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                        <FileSpreadsheet className="w-12 h-12 mb-4 opacity-50" />
                        <p>No assets found</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Type</TableHead>
                                <TableHead className="min-w-[280px]">Filename</TableHead>
                                <TableHead>Size</TableHead>
                                <TableHead>Records</TableHead>
                                <TableHead>Uploaded By</TableHead>
                                <TableHead>Uploaded At</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {assets.map((asset) => (
                                <TableRow key={asset.id} className={asset.status === 'deleted' ? 'opacity-60' : ''}>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {getFileTypeIcon(asset.file_type)}
                                            <span className="capitalize">{asset.file_type === 'content' ? 'Metadata' : asset.file_type}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-medium min-w-[280px] max-w-[420px]" title={asset.filename}>
                                        <span className="break-words whitespace-normal block leading-snug">
                                            {asset.filename}
                                        </span>
                                    </TableCell>
                                    <TableCell>{formatFileSize(asset.file_size)}</TableCell>
                                    <TableCell>{asset.records_processed?.toLocaleString() || 0}</TableCell>
                                    <TableCell>
                                        <div className="text-sm">
                                            <div>{asset.uploaded_by_name}</div>
                                            <div className="text-muted-foreground text-xs">{asset.uploaded_by_email}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm">{formatDate(asset.uploaded_at)}</TableCell>
                                    <TableCell>
                                        {getStatusBadge(asset.status)}
                                        {asset.status === 'delete_requested' && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                <div>Requested by: {asset.delete_requested_by}</div>
                                                <div>At: {formatDate(asset.delete_requested_at)}</div>
                                                {asset.delete_request_reason && (
                                                    <div className="text-yellow-400">Reason: {asset.delete_request_reason}</div>
                                                )}
                                            </div>
                                        )}
                                        {asset.status === 'deleted' && (
                                            <div className="text-xs text-red-400 mt-1">
                                                <div>Deleted by: {asset.deleted_by}</div>
                                                <div>At: {formatDate(asset.deleted_at)}</div>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {asset.status === 'active' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownload(asset)}
                                                    title="Download"
                                                >
                                                    <Download className="w-4 h-4" />
                                                </Button>
                                            )}
                                            
                                            {asset.status === 'active' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        if (isExecutive) {
                                                            setRestrictedDialogOpen(true);
                                                            return;
                                                        }
                                                        openDeleteModal(asset);
                                                    }}
                                                    className="text-destructive hover:text-destructive"
                                                    title={
                                                        isExecutive
                                                            ? "Requires Founder/Admin approval"
                                                            : isFounder
                                                                ? "Delete"
                                                                : "Request Delete"
                                                    }
                                                    data-testid={`delete-asset-${asset.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                            
                                            {asset.status === 'delete_requested' && isFounder && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openApprovalModal(asset, 'approve')}
                                                        className="text-green-500 hover:text-green-400"
                                                        title="Approve Delete"
                                                    >
                                                        <CheckCircle className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openApprovalModal(asset, 'reject')}
                                                        className="text-red-500 hover:text-red-400"
                                                        title="Reject Delete"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Pagination */}
            {total > limit && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => p + 1)}
                            disabled={page * limit >= total}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}

            {/* Executive Restricted Action Dialog */}
            <Dialog
                open={restrictedDialogOpen}
                onOpenChange={setRestrictedDialogOpen}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Founder approval required</DialogTitle>
                        <DialogDescription>
                            Executive accounts have read-only access.
                            Asset deletion must be initiated by Admin and approved by Founder.
                        </DialogDescription>
                    </DialogHeader>

                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={() => setRestrictedDialogOpen(false)}
                        >
                            Understood
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Request Modal */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Request Asset Deletion</DialogTitle>
                        <DialogDescription>
                            This will submit a deletion request that requires founder approval.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedAsset && (
                        <div className="space-y-4">
                            <div className="p-4 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    {getFileTypeIcon(selectedAsset.file_type)}
                                    <span className="font-medium">{selectedAsset.filename}</span>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {selectedAsset.records_processed.toLocaleString()} records � 
                                    Uploaded {formatDate(selectedAsset.uploaded_at)}
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="reason">Reason for deletion *</Label>
                                <Input
                                    id="reason"
                                    placeholder="Enter reason for deletion request..."
                                    value={deleteReason}
                                    onChange={(e) => setDeleteReason(e.target.value)}
                                />
                            </div>
                            
                            <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 text-sm">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                <span>This action requires founder approval before execution.</span>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleRequestDelete} 
                            disabled={submitting || !deleteReason.trim()}
                            className="bg-destructive hover:bg-destructive/90"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Request'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approval Modal */}
            <Dialog open={approvalModalOpen} onOpenChange={setApprovalModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {approvalAction === 'approve' ? 'Approve Deletion' : 'Reject Deletion Request'}
                        </DialogTitle>
                        <DialogDescription>
                            {approvalAction === 'approve' 
                                ? 'This will permanently delete the asset and its file content.'
                                : 'This will reject the deletion request and keep the asset active.'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedAsset && (
                        <div className="space-y-4">
                            <div className="p-4 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    {getFileTypeIcon(selectedAsset.file_type)}
                                    <span className="font-medium">{selectedAsset.filename}</span>
                                </div>
                                <div className="text-sm text-muted-foreground mb-2">
                                    {selectedAsset.records_processed.toLocaleString()} records
                                </div>
                                <div className="text-sm">
                                    <span className="text-muted-foreground">Requested by: </span>
                                    {selectedAsset.delete_requested_by}
                                </div>
                                <div className="text-sm">
                                    <span className="text-muted-foreground">Reason: </span>
                                    {selectedAsset.delete_request_reason}
                                </div>
                            </div>
                            
                            {approvalAction === 'approve' && (
                                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                                    <AlertCircle className="w-4 h-4 shrink-0" />
                                    <span>This action cannot be undone. The file will be permanently deleted.</span>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApprovalModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleApproval} 
                            disabled={submitting}
                            className={approvalAction === 'approve' 
                                ? 'bg-destructive hover:bg-destructive/90' 
                                : 'bg-primary hover:bg-primary/90'
                            }
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Processing...
                                </>
                            ) : approvalAction === 'approve' ? (
                                'Approve & Delete'
                            ) : (
                                'Reject Request'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AssetsPage;